import { useEffect, useRef } from "react";
import { usePostHog } from "@posthog/react";
import { useSlice } from "@bridge-applications/provider-kit";
import { POSTHOG_ENABLED, TELEGRAM_MINI_APP_URL } from "../../state/config";
import { captureClientAnalytics } from "../helpers/analytics";
import { getTelegramWebApp } from "../helpers/telegram";
import { createWriteAccessRequester } from "../helpers/telegramWriteAccess";

/** A local-only prompt after gameplay; never joins login or spin network work. */
export function useTelegramWriteAccess(
    completedSpinUserId: string | null,
    busy: boolean,
) {
    const { state: auth } = useSlice("authSlice");
    const posthog = usePostHog();
    const requester = useRef<ReturnType<
        typeof createWriteAccessRequester
    > | null>(null);
    if (!requester.current) requester.current = createWriteAccessRequester();
    const finishedIdentity = useRef<string | null>(null);
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const identity = `${auth.userId}:${auth.sessionGeneration}`;
    const currentIdentity = useRef(identity);
    currentIdentity.current = identity;

    useEffect(() => {
        if (
            finishedIdentity.current === identity ||
            busy ||
            auth.bootstrapStatus !== "ready" ||
            !auth.userId ||
            completedSpinUserId !== auth.userId ||
            !auth.telegramUserId
        )
            return;
        let active = true;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let observer: MutationObserver | undefined;
        let dialogOpen = Boolean(document.querySelector("dialog[open]"));
        const attempt = () => {
            clearTimeout(timer);
            if (
                document.visibilityState !== "visible" ||
                document.querySelector("dialog[open]")
            )
                return;
            timer = setTimeout(() => {
                if (
                    !active ||
                    currentIdentity.current !== identity ||
                    document.visibilityState !== "visible" ||
                    document.querySelector("dialog[open]")
                )
                    return;
                let storage: Storage | null = null;
                try {
                    storage = window.localStorage;
                } catch {
                    /* Private browser mode. */
                }
                finishedIdentity.current = identity;
                observer?.disconnect();
                document.removeEventListener("visibilitychange", attempt);
                const canCapture = () =>
                    mounted.current &&
                    currentIdentity.current === identity &&
                    POSTHOG_ENABLED &&
                    posthog;
                requester.current?.({
                    bridge: getTelegramWebApp(),
                    scope: TELEGRAM_MINI_APP_URL,
                    telegramUserId: String(auth.telegramUserId),
                    storage,
                    onRequested: () => {
                        if (canCapture())
                            captureClientAnalytics(
                                posthog,
                                "telegram_write_access_requested",
                                { trigger: "first_completed_spin" },
                            );
                    },
                    onResult: (result) => {
                        if (canCapture())
                            captureClientAnalytics(
                                posthog,
                                "telegram_write_access_result",
                                { trigger: "first_completed_spin", result },
                            );
                    },
                });
            }, 1000);
        };
        // Existing sheets all use native dialog. Observe only while eligible.
        observer = new MutationObserver(() => {
            const nextOpen = Boolean(document.querySelector("dialog[open]"));
            if (nextOpen === dialogOpen) return;
            dialogOpen = nextOpen;
            attempt();
        });
        observer.observe(document.body, {
            subtree: true,
            attributes: true,
            attributeFilter: ["open"],
            childList: true,
        });
        document.addEventListener("visibilitychange", attempt);
        attempt();
        return () => {
            active = false;
            clearTimeout(timer);
            observer.disconnect();
            document.removeEventListener("visibilitychange", attempt);
        };
    }, [
        auth.bootstrapStatus,
        auth.userId,
        auth.telegramUserId,
        identity,
        completedSpinUserId,
        busy,
        posthog,
    ]);
}
